import React, {useEffect, useMemo, useState, useCallback, useRef} from "react";
import {Link, useParams} from "react-router-dom";
import {doc, onSnapshot, collection, query, where, getDocs} from "firebase/firestore";
import db from "../../firebase";
import styles from "./BucketRoomPage.module.css";
import logo from "assets/pokerly-logo-purple.svg";

import {
    joinRoom,
    revealBucketIteration,
    finishBucket,
    setBucketCurrentIteration,
    voteBucket,
    startBucketVoting,
    revoteBucketIteration,
} from "roomActions";

import EstimatePanel from "components/common/EstimatePanel";
import IconCopy from "assets/Icon_copy.svg";
import IconList from "assets/icon-list.svg";
import IconSettings from "assets/Icon_settings.svg";
import IconBucket from "assets/icon-bucket.svg";
import IconRefresh from "assets/icon-refresh.svg";
import TaskManagerModal from "../../components/bucket/TaskManagerModal/TaskManagerModal";
import ParticipantsList from "components/common/ParticipantsList/ParticipantsList";
import {Trans, useTranslation} from "react-i18next";
import AiTooltip from "components/common/AiTooltip/AiTooltip";
import BurgerMenu from "components/common/BurgerMenu/BurgerMenu"
import RoomSettingsModal from "components/common/RoomSettingsModal/RoomSettingsModal";
import {Stats, VoteStats} from "utils/VoteStats";
import {VoteParser} from "utils/VoteParser";
import {getScaleValues} from "utils/scales"
import footerBg from "../../assets/footer-bg.svg";

type SetupStatus = "draft" | "ready" | "inProgress" | "finished";

interface Participant {
    id: string;
    name: string;
    role: "facilitator" | "participant";
}

interface IterationDoc {
    id: string;
    index: number;
    status: "voting" | "revealed" | "completed";
    taskIds: string[];
    estimatesByTask: Record<string, Record<string, number | string>>;
    estimatesByUser: Record<string, Record<string, number | string>>;
}

interface TaskDoc {
    id: string;
    title: string;
    description: string;
    iterationId: string;
    iterationIndex: number;
}

const BucketRoomPage: React.FC = () => {
    const {roomId} = useParams<{ roomId: string }>();
    const {t} = useTranslation();

    const [userId, setUserId] = useState<string>("");
    const [userName, setUserName] = useState<string>("");

    const [roomName, setRoomName] = useState("");
    const [estimationType, setEstimationType] = useState("fibonacci");
    const [participants, setParticipants] = useState<Participant[]>([]);
    const [setupStatus, setSetupStatus] = useState<SetupStatus>("draft");
    const [currentIteration, setCurrentIteration] = useState<string | null>(null);
    const [totalIterations, setTotalIterations] = useState(0);
    const [showTaskManager, setShowTaskManager] = useState(false);
    const hasJoinedRef = useRef(false);

    const [iterationDoc, setIterationDoc] = useState<IterationDoc | null>(null);
    const [tasks, setTasks] = useState<TaskDoc[]>([]);

    const [hoveredTaskId, setHoveredTaskId] = useState<string | null>(null);
    const [hoveredResultTaskId, setHoveredResultTaskId] = useState<string | null>(null);

    const [settingsOpen, setSettingsOpen] = useState(false);
    const [ownerId, setOwnerId] = useState<string>("");
    const [customValues, setCustomValues] = useState<string[]>([]);

    const [isActiveCopy, setIsActiveCopy] = useState(false);

    const copyButtonClick = (room: string) => {
        navigator.clipboard.writeText(room)

        setIsActiveCopy(true);

        setTimeout(() => {
            setIsActiveCopy(false);
        }, 1000);
    };

    // ---------------------- user init ----------------------
    useEffect(() => {
        let id = localStorage.getItem("userId");
        if (!id) {
            id = Math.random().toString(36).substring(2, 10);
            localStorage.setItem("userId", id);
        }
        setUserId(id);

        let name = localStorage.getItem("userName");
        if (!name || !name.trim()) {
            const suggested = `${t("defaultUserName")}-${id.slice(0, 4)}`;
            const entered = prompt(t("userNamePrompt"), suggested)?.trim();
            name = entered && entered.length > 0 ? entered : suggested;
            localStorage.setItem("userName", name);
        }
        setUserName(name);
    }, []);

    // ---------------------- room subscription ----------------------
    useEffect(() => {
        if (!roomId) return;
        const ref = doc(db, "rooms", roomId);
        return onSnapshot(ref, (snap) => {
            if (!snap.exists()) return;

            const data = snap.data() as any;
            setRoomName(data.roomName || "");
            setEstimationType(data.estimationType || "fibonacci");
            setSetupStatus((data.setupStatus as SetupStatus) || "draft");
            setCurrentIteration(data.currentIteration ?? null);
            setTotalIterations(data.totalIterations || 0);

            const partisipantList = Object.values(data.participants || {}) as Participant[];
            setParticipants(partisipantList);
            setOwnerId(data.owner || "");

            if (data.customValues && Array.isArray(data.customValues)) {
                setCustomValues(data.customValues);
            }
        });
    }, [roomId, userId]);

    useEffect(() => {
        const addParticipantIfNeeded = async () => {
            if (!userId || !userName || !roomId || hasJoinedRef.current) return;

            try {
                await joinRoom(roomId, userId, userName);
                hasJoinedRef.current = true;
            } catch (error) {
                console.error("Error entering the room " + roomId + ":", error);
            }
        };
        addParticipantIfNeeded();
    }, [roomId, userId, userName]);

    // ---------------------- current iteration subscription ----------------------
    useEffect(() => {
        if (!roomId || currentIteration == null) {
            setIterationDoc(null);
            setTasks([]);
            return;
        }
        // подписка на итерацию
        const iterRef = doc(db, "rooms", roomId, "iterations", currentIteration);
        const iterationSync = onSnapshot(iterRef, (snap) => {
            if (!snap.exists()) return;
            const data = snap.data() as any;
            setIterationDoc({
                id: snap.id,
                index: data.index,
                status: data.status,
                taskIds: data.taskIds || [],
                estimatesByTask: data.estimatesByTask || {},
                estimatesByUser: data.estimatesByUser || {},
            });
        });
        // подписка на таски текущей итерации
        const tasksRef = query(
            collection(db, "rooms", roomId, "tasks"),
            where("iterationId", "==", currentIteration)
        );
        const tasksSync = onSnapshot(tasksRef, (snap) => {
            const rows: TaskDoc[] = snap.docs.map((d) => ({id: d.id, ...(d.data() as any)}));
            rows.sort((a, b) => (a.iterationIndex ?? 0) - (b.iterationIndex ?? 0));
            setTasks(rows);
        });

        return () => {
            iterationSync();
            tasksSync();
        };
    }, [roomId, currentIteration]);

    const me = participants.find((p) => p.id === userId);
    const isFacilitator = me?.role === "facilitator";
    const canNavigate = setupStatus === "ready" || (setupStatus === "inProgress" && isFacilitator);

    // ---------------------- vote handlers ----------------------
    const handleVote = useCallback(
        async (taskId: string, value: number | string) => {
            if (!roomId || !iterationDoc) return;
            if (iterationDoc.status !== "voting") return;
            await voteBucket({
                roomId,
                iterationId: iterationDoc.id,
                taskId,
                userId,
                value,
            });
        },
        [roomId, iterationDoc, userId]
    );

    const handleReveal = async () => {
        if (!roomId || !iterationDoc) return;
        await revealBucketIteration(roomId, iterationDoc.id);
    };

    const handleStartVoting = async () => {
        if (!roomId) return;
        // старт — синхронизация по фасилитатору
        await startBucketVoting(roomId);
    };

    const handlePrev = async () => {
        if (!roomId || iterationDoc == null || !canNavigate) return;
        const prevIndex = Math.max(0, iterationDoc.index - 1);
        if (setupStatus === "ready") {
            setCurrentIteration(String(prevIndex));
        } else {
            await setBucketCurrentIteration(roomId, String(prevIndex));
        }
    };
    const handleNext = async () => {
        if (!roomId || iterationDoc == null || !canNavigate) return;
        const nextIndex = Math.min(totalIterations - 1, iterationDoc.index + 1);
        if (setupStatus === "ready") {
            setCurrentIteration(String(nextIndex));
        } else {
            await setBucketCurrentIteration(roomId, String(nextIndex));
        }
    };
    const handleFinish = async () => {
        if (!roomId) return;
        await finishBucket(roomId);
    };
    const handleRevoteIteration = async (index: number) => {
        if (!roomId || !isFacilitator) return;
        await revoteBucketIteration(roomId, String(index));
    };


    // ---------------------- aggregates for Results ----------------------
    const [allIterations, setAllIterations] = useState<IterationDoc[]>([]);
    const [allTasks, setAllTasks] = useState<TaskDoc[]>([]);

    useEffect(() => {
        if (!roomId || (setupStatus !== "ready" && setupStatus !== "finished")) return;

        // подписка на все итерации
        const iterRef = collection(db, "rooms", roomId, "iterations");
        const unsubIter = onSnapshot(iterRef, (snap) => {
            const arr: IterationDoc[] = snap.docs.map((d) => ({id: d.id, ...(d.data() as any)}));
            arr.sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
            setAllIterations(arr);
        });

        // подписка на все таски
        const tasksRef = collection(db, "rooms", roomId, "tasks");
        const unsubTasks = onSnapshot(tasksRef, (snap) => {
            const rows: TaskDoc[] = snap.docs.map((d) => ({id: d.id, ...(d.data() as any)}));
            setAllTasks(rows);
        });

        return () => {
            unsubIter();
            unsubTasks();
        };
    }, [roomId, setupStatus]);

    type AggregatedTask = {
        stats: Stats;
        votes: Record<string, number | string | null | undefined>,
    };

    const aggregatesByTask: Record<string, AggregatedTask> = useMemo(() => {
        if (setupStatus !== "finished") return {} as Record<string, AggregatedTask>;

        const acc: Record<string, AggregatedTask> = {};

        for (const iter of allIterations) {
            for (const [taskId, votesByUser] of Object.entries(iter.estimatesByTask || {})) {

                const stats = VoteStats.calcStatsFromVotes(votesByUser, getScaleValues(estimationType, customValues));
                acc[taskId] = {
                    stats,
                    votes: votesByUser,
                };
            }
        }

        return acc;
    }, [setupStatus, allIterations]);

    const votedMap = useMemo(() => {
        if (!iterationDoc) return {};
        const taskIds = iterationDoc.taskIds || [];
        const byTask = iterationDoc.estimatesByTask || {};
        const map: Record<string, boolean> = {};

        participants.forEach((p) => {
            if (taskIds.length === 0) {
                map[p.id] = false;
                return;
            }
            const allVoted = taskIds.every((tid) => {
                const taskVotes = byTask[tid] || {};
                return taskVotes[p.id] !== undefined && taskVotes[p.id] !== null;
            });
            map[p.id] = allVoted;
        });

        return map;
    }, [iterationDoc, participants]);

    const ResultCard: React.FC<{
        task: TaskDoc;
        stats: Stats;
        hovered: boolean;
        onHover: (id: string | null) => void;
        myVote: null | string | number;

    }> = ({task, stats, hovered, onHover, myVote}) => {
        const {average, median, range, recommended, spread} = stats;

        return (
            <div className={styles.resultTaskCardWrp}
                 onMouseEnter={() => onHover(task.id)}
                 onMouseLeave={() => onHover(null)}
            >
                <div className={`${styles.taskCard} ${spread ? styles[spread] : ''}`}>
                    <div className={styles.taskContentWrp}>
                        <div className={styles.taskIndex}>
                            {task.iterationIndex + 1}
                            <span></span>
                        </div>
                        <div className={styles.taskText}>
                            <div className={`${styles.taskTitle}`}>
                                {task.title}
                            </div>
                            <div className={styles.taskDesc}>{task.description}</div>
                        </div>
                    </div>
                    <div className={styles.taskCardFooter}>
                        {myVote != null && (
                            <div className={styles.myVotePill}>
                                <svg width="12" height="8" viewBox="0 0 12 8" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M11.3546 0.146894C11.4012 0.193339 11.4381 0.248515 11.4633 0.30926C11.4886 0.370005 11.5015 0.435127 11.5015 0.500894C11.5015 0.566661 11.4886 0.631782 11.4633 0.692528C11.4381 0.753273 11.4012 0.808448 11.3546 0.854894L4.35463 7.85489C4.30819 7.90146 4.25301 7.9384 4.19227 7.96361C4.13152 7.98881 4.0664 8.00179 4.00063 8.00179C3.93486 8.00179 3.86974 7.98881 3.809 7.96361C3.74825 7.9384 3.69308 7.90146 3.64663 7.85489L0.146631 4.35489C0.0527448 4.26101 0 4.13367 0 4.00089C0 3.86812 0.0527448 3.74078 0.146631 3.64689C0.240518 3.55301 0.367856 3.50026 0.500632 3.50026C0.633407 3.50026 0.760745 3.55301 0.854632 3.64689L4.00063 6.79389L10.6466 0.146894C10.6931 0.100331 10.7483 0.0633878 10.809 0.0381813C10.8697 0.0129749 10.9349 0 11.0006 0C11.0664 0 11.1315 0.0129749 11.1923 0.0381813C11.253 0.0633878 11.3082 0.100331 11.3546 0.146894Z"
                                        fill="#484848"/>
                                </svg>
                                {t("yourVote")}: <b>{myVote}</b>
                            </div>
                        )}
                        {recommended != null ? (
                            <div className={styles.resultBigNumber}>
                                <svg width="18" height="20" viewBox="0 0 18 20" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M8.34 13.0213C8.73 13.4113 9.36 13.4113 9.75 13.0213L16.11 6.66126C16.5 6.27126 16.5 5.64126 16.11 5.25126L11.16 0.301262C10.78 -0.0987381 10.15 -0.0987381 9.76 0.291262L3.39 6.66126C3 7.05126 3 7.68126 3.39 8.07125L8.34 13.0213ZM10.46 2.41126L14 5.95126L9.05 10.9013L5.51 7.36126L10.46 2.41126ZM17.41 13.4113L15.29 11.2913C15.11 11.1113 14.85 11.0013 14.59 11.0013H14.32L12.32 13.0013H14.23L16 15.0013H2L3.78 13.0013H5.83L3.83 11.0013H3.41C3.14 11.0013 2.89 11.1113 2.7 11.2913L0.58 13.4113C0.21 13.7913 0 14.3013 0 14.8313V18.0013C0 19.1013 0.9 20.0013 2 20.0013H16C17.1 20.0013 18 19.1113 18 18.0013V14.8313C18 14.3013 17.79 13.7913 17.41 13.4113Z"
                                        fill="#9865DA"/>
                                </svg>
                                {VoteParser.formatMaybeNumber(recommended)}
                            </div>
                        ) : (
                            <div className={styles.resultBigNumber}>
                                <svg width="18" height="20" viewBox="0 0 18 20" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M8.34 13.0213C8.73 13.4113 9.36 13.4113 9.75 13.0213L16.11 6.66126C16.5 6.27126 16.5 5.64126 16.11 5.25126L11.16 0.301262C10.78 -0.0987381 10.15 -0.0987381 9.76 0.291262L3.39 6.66126C3 7.05126 3 7.68126 3.39 8.07125L8.34 13.0213ZM10.46 2.41126L14 5.95126L9.05 10.9013L5.51 7.36126L10.46 2.41126ZM17.41 13.4113L15.29 11.2913C15.11 11.1113 14.85 11.0013 14.59 11.0013H14.32L12.32 13.0013H14.23L16 15.0013H2L3.78 13.0013H5.83L3.83 11.0013H3.41C3.14 11.0013 2.89 11.1113 2.7 11.2913L0.58 13.4113C0.21 13.7913 0 14.3013 0 14.8313V18.0013C0 19.1013 0.9 20.0013 2 20.0013H16C17.1 20.0013 18 19.1113 18 18.0013V14.8313C18 14.3013 17.79 13.7913 17.41 13.4113Z"
                                        fill="#9865DA"/>
                                </svg>
                                {t("noDataLabel")}
                            </div>
                        )}
                    </div>
                </div>
                {hovered && (
                    <div className={styles.tooltipHost}>
                        <AiTooltip
                            recommendedValue={recommended}
                            explanationText={VoteStats.explainSpread(spread, range ?? null, t)}
                            average={average}
                            median={median}
                            spread={spread}
                            range={stats.range}
                            className={styles.tooltipCompact}
                        />
                    </div>
                )}
            </div>
        );
    };

    // ---------------------- UI ----------------------
    if (!roomId) return null;


    return (
        <div className={styles.container}>
            <div className={styles.logo}>
                <Link to="/"><img src={logo} alt="Logo"/></Link>
            </div>
            <BurgerMenu/>
            <div className={styles.bucketWrp}>
                {/* Header */}
                <div className={styles.header}>
                    <div className={styles.headerRow}>
                        <div className={styles.roomId}>
                            <svg width="9" height="10" viewBox="0 0 9 10" fill="none"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M0.701947 5.01589C0.594946 4.99367 0.501508 4.98179 0.413345 4.95382C0.145842 4.87069 -0.0026028 4.65653 3.45502e-05 4.36689C0.0026719 4.07726 0.153754 3.86693 0.424271 3.788C0.514695 3.76425 0.608132 3.75314 0.70157 3.75506C3.09365 3.75352 5.48572 3.75352 7.87743 3.75506C7.98782 3.75314 8.09783 3.77038 8.20257 3.80639C8.45538 3.90179 8.57218 4.10292 8.57143 4.3734C8.57067 4.64388 8.45274 4.84195 8.20069 4.93965C8.09934 4.97796 7.98857 4.9929 7.86009 5.02317C7.78926 5.71546 7.71843 6.41503 7.64685 7.11345C7.57865 7.77892 7.51385 8.44439 7.44113 9.10871C7.37972 9.66921 7.01803 9.99716 6.45966 9.99793C5.00761 10.0002 3.55556 10.0002 2.10276 9.99793C1.54854 9.99793 1.18684 9.665 1.12769 9.10143C0.993939 7.82336 0.865462 6.5449 0.734348 5.26645C0.726813 5.18216 0.712873 5.09788 0.701947 5.01627V5.01589Z"
                                    fill="#484848"/>
                                <path
                                    d="M7.63178 3.10171H6.07581C6.05659 2.93429 6.04491 2.77224 6.01854 2.61248C5.85578 1.61982 5.05703 1.64144 4.78231 1.63799C4.35582 1.63799 3.92141 1.59968 3.5032 1.66251C2.52964 1.80886 2.61096 2.53678 2.52167 2.82319C2.51413 2.90901 2.50923 2.99521 2.5017 3.09712H0.929521C0.905785 2.59255 0.994324 2.11902 1.21586 1.68189C1.75539 0.616826 2.62948 0.0486657 3.80084 0.00728919C4.35167 -0.0118666 4.9187 0.00115934 5.45484 0.114562C6.76409 0.390788 7.74255 1.76158 7.63178 3.10171Z"
                                    fill="#484848"/>
                            </svg>
                            {t("roomWithId", { id: roomId })}
                        </div>
                        <div className={styles.toolBtnGroup}>
                            <button
                                className={styles.copyBtn}
                                onClick={() => copyButtonClick(roomId)}
                            >
                                <svg width="11" height="11" viewBox="0 0 11 11" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M2 7H1.5C1.23478 7 0.98043 6.89464 0.792893 6.70711C0.605357 6.51957 0.5 6.26522 0.5 6V1.5C0.5 1.23478 0.605357 0.98043 0.792893 0.792893C0.98043 0.605357 1.23478 0.5 1.5 0.5H6C6.26522 0.5 6.51957 0.605357 6.70711 0.792893C6.89464 0.98043 7 1.23478 7 1.5V2M5 4H9.5C10.0523 4 10.5 4.44772 10.5 5V9.5C10.5 10.0523 10.0523 10.5 9.5 10.5H5C4.44772 10.5 4 10.0523 4 9.5V5C4 4.44772 4.44772 4 5 4Z"
                                        stroke="#9865DA" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                {t("copyRoomId")}
                            </button>
                            <div className={`${styles.copyTooltip} ${isActiveCopy ? styles.active : ''}`}>
                                <svg width="8" height="5.5" viewBox="0 0 9 7" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8.5 0.5L3 6L0.5 3.5" stroke="#5CC862" stroke-linecap="round"
                                          stroke-linejoin="round"/>
                                </svg>
                                {t("copy")}
                            </div>
                        </div>
                    </div>
                    <div className={styles.roomInfo}>
                        <h1 className={styles.title}>
                            {roomName || t("untitledRoom")}
                        </h1>
                        <div className={styles.toolBtnWrp}>
                            <div className={styles.toolBtnGroup}>
                                <button
                                    className={`${styles.toolBtn} ${styles.managerBtn}`}
                                    disabled={!isFacilitator}
                                    onClick={() => setShowTaskManager(true)}
                                >
                                    <img src={IconList} alt=""/> {t("taskManagementButton")}
                                </button>
                                <button
                                    className={styles.toolBtn}
                                    onClick={() => setSettingsOpen(true)}
                                    title={t("settings") as string}
                                >
                                    <img src={IconSettings} alt=""/> {t("settings")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                {/* BODY */}
                {setupStatus === "draft" && (
                    <>
                        {isFacilitator && (
                            <div className={styles.taskAreaPlaceholder}>
                                <p>
                                    <Trans
                                        i18nKey="addFirstTask"
                                        components={{bold: <b/>, span: <span/>}}
                                    />
                                </p>
                                <svg width="34" height="38" viewBox="0 0 34 38" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M19.5215 2.02285C18.737 2.41892 17.7714 2.41892 17.178 3.22949L17.2183 3.24792C16.7556 3.86506 16.0616 4.34403 15.4682 4.88748C14.7943 5.50462 13.9997 6.05729 13.4666 6.76654C12.8632 7.5679 11.7266 7.22709 11.093 7.93634C10.4996 8.60875 9.75527 9.17062 9.07133 9.77855C7.31119 9.66802 6.89881 11.2892 5.73209 11.9247C4.78664 12.4313 5.28954 13.5459 4.54525 14.1077C4.7263 14.6972 4.95763 15.296 5.1789 15.8394C5.44041 16.4934 6.27522 16.9632 6.8787 17.4882C7.22066 17.7829 8.47791 17.7645 8.83999 17.4606L8.84001 17.4606C9.19203 17.1658 9.54405 16.8711 9.84579 16.5302C10.3286 15.9868 10.8918 15.7934 11.6663 15.7473C12.1591 15.7197 12.3301 14.817 13.0543 14.8999C13.7986 14.9828 13.6376 14.5499 13.7181 14.1538C13.7382 14.0433 14.3216 14.0985 14.5328 13.9419C15.3475 13.3616 16.132 12.7169 16.9266 12.0997C18.1235 12.284 18.5057 11.2984 19.1795 10.7549C19.8937 10.1838 20.5374 9.52064 21.2213 8.91271C21.2917 8.85744 21.4929 8.8206 21.5331 8.85744C21.6437 8.97719 21.7242 9.13378 21.7846 9.28115C22.0964 10.0825 21.161 10.6168 21.2716 11.3905C21.3621 12.0353 21.0101 12.5971 20.8492 13.1866C20.6279 13.9788 20.2759 14.7341 20.0646 15.5262C19.8635 16.3092 19.5517 17.0829 19.2198 17.829C18.8879 18.5843 18.7068 19.3765 18.2944 20.1133C18.0531 20.5371 18.0832 21.0805 17.6407 21.5134C16.9366 22.2043 15.8001 22.6556 15.7196 23.7241C15.6593 24.6175 14.2914 24.9307 14.6535 25.9716C14.6937 26.0821 14.3819 26.4321 14.2411 26.4321C12.6922 26.3953 12.0082 27.5467 11.1936 28.3849C10.6303 28.9652 9.82567 28.5875 9.33283 29.0849C8.93051 29.4902 8.48796 30.1718 8.06553 30.1718C7.30113 30.181 6.74794 30.531 6.13441 30.7337C4.17311 31.3969 2.20178 31.8114 0.0896103 31.6548C0.0896103 31.6548 -0.292614 38.2591 0.522079 37.992C1.08532 37.8078 1.61839 37.6143 2.16152 37.6972C3.59981 37.9183 4.83693 36.9511 6.26516 37.1998C6.43615 37.2275 6.74794 37.0801 6.83846 36.9327C7.40171 35.9748 8.90034 36.5274 9.45353 35.5142C9.72509 35.0168 10.2783 34.805 10.9522 34.8787C11.616 34.9523 12.4206 35.0813 12.139 34.0128C13.4264 34.1786 13.9293 33.1838 14.6736 32.5575C15.3777 31.968 16.0918 31.3692 16.6852 30.6876C17.1277 30.181 17.6809 30.2731 18.224 30.181C18.1536 29.6099 18.2844 29.1494 18.8074 28.7533C19.3606 28.3296 19.9138 27.8783 20.3161 27.3532C20.6882 26.865 20.7888 26.2018 21.161 25.7137C21.8851 24.7926 21.9455 23.4662 23.2128 22.8767C23.253 22.2227 23.4341 21.6884 23.9872 21.1818C24.3393 20.8594 24.3292 20.2331 24.5807 19.7817C25.0735 18.8975 24.993 18.1514 24.1381 17.5066C23.6754 17.1566 23.7056 16.7882 24.1482 16.4473C24.631 16.0789 24.9629 15.6736 24.8522 15.0565C24.8522 15.0288 25.1741 14.9183 25.3551 14.9183C25.4356 14.9183 25.5764 15.0473 25.5965 15.1394C25.9284 16.4289 26.7129 17.5987 26.8538 18.9343C27.0046 20.2976 27.6584 21.6055 27.3667 23.0148C27.9501 23.7517 27.8797 24.5991 27.8797 25.4465C27.8897 26.9572 28.3926 27.2888 30.022 26.865C30.2232 26.8098 30.3841 26.6071 30.5852 26.5334C30.9071 26.4137 30.9976 26.5427 30.9172 26.8743C30.7462 27.5467 31.2491 27.473 31.7117 27.4546C32.1241 27.4453 32.1644 27.473 32.4057 26.7914L32.4115 26.7749C32.4874 26.5571 32.5146 26.4791 32.8483 26.515C33.5423 26.5795 33.8843 26.3032 33.9748 25.6216C34.1659 24.2123 33.2003 22.9411 33.4518 21.5411C32.6572 20.7121 33.0394 19.7081 32.9388 18.7777L32.9378 18.768C32.9077 18.474 32.8661 18.0669 32.6572 17.9211C32.0034 17.4606 31.8324 16.8987 31.933 16.2079C32.1141 15.0104 31.5408 13.9604 31.239 12.855C30.9675 11.8602 30.3539 10.976 30.374 9.87987C30.3841 9.18904 30.0421 8.489 29.7907 7.8166C29.5392 7.14419 28.8553 6.46257 28.9559 5.86385C29.1872 4.43614 28.3725 3.63478 27.3969 2.80579C26.6124 2.13338 25.858 1.43334 25.1137 0.714878C24.6008 0.226693 23.6754 0.714878 23.253 0.0148384C23.2228 -0.0312168 22.7702 0.0332605 22.6395 0.162215C22.1265 0.631979 21.6538 0.963576 20.8291 0.99121C20.3764 1.00963 20.0244 1.76494 19.5215 2.02285Z"
                                        fill="#A765DA"/>
                                </svg>
                            </div>
                        )}
                        <div className={styles.tasksWrp}>
                        </div>
                        <ParticipantsList
                            participants={participants}
                            currentUserId={userId}
                            votedMap={{}}
                        />
                    </>
                )}

                {(setupStatus === "inProgress" || setupStatus === "ready") && iterationDoc && (
                    <>
                        <div className={styles.tasksWrp}>
                            {/* старт доступен только фасилитатору */}
                            {isFacilitator && setupStatus === "ready" && (
                                <div className={styles.primaryCtaWrp}>
                                    <div className={styles.primaryCtaBg}></div>
                                    <div className={`${styles.taskAreaPlaceholder} ${styles.taskAreaPlaceholderCta}`}>
                                        <p>
                                            <Trans
                                                i18nKey="tasksReady"
                                                count={allTasks.length}
                                                components={{bold: <b/>}}
                                            />
                                        </p>
                                        <svg width="34" height="38" viewBox="0 0 34 38" fill="none"
                                             xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M19.5215 35.9772C18.737 35.5811 17.7714 35.5811 17.178 34.7705L17.2183 34.7521C16.7556 34.1349 16.0616 33.656 15.4682 33.1125C14.7943 32.4954 13.9997 31.9427 13.4666 31.2335C12.8632 30.4321 11.7266 30.7729 11.093 30.0637C10.4996 29.3913 9.75527 28.8294 9.07133 28.2215C7.31119 28.332 6.89881 26.7108 5.73209 26.0753C4.78664 25.5687 5.28954 24.4541 4.54525 23.8923C4.7263 23.3028 4.95763 22.704 5.1789 22.1606C5.44041 21.5066 6.27522 21.0368 6.8787 20.5118C7.22066 20.2171 8.47791 20.2355 8.83999 20.5394L8.84001 20.5394C9.19203 20.8342 9.54405 21.1289 9.84579 21.4698C10.3286 22.0132 10.8918 22.2066 11.6663 22.2527C12.1591 22.2803 12.3301 23.183 13.0543 23.1001C13.7986 23.0172 13.6376 23.4501 13.7181 23.8462C13.7382 23.9567 14.3216 23.9015 14.5328 24.0581C15.3475 24.6384 16.132 25.2831 16.9266 25.9003C18.1235 25.716 18.5057 26.7016 19.1795 27.2451C19.8937 27.8162 20.5374 28.4794 21.2213 29.0873C21.2917 29.1426 21.4929 29.1794 21.5331 29.1426C21.6437 29.0228 21.7242 28.8662 21.7846 28.7188C22.0964 27.9175 21.161 27.3832 21.2716 26.6095C21.3621 25.9647 21.0101 25.4029 20.8492 24.8134C20.6279 24.0212 20.2759 23.2659 20.0646 22.4738C19.8635 21.6908 19.5517 20.9171 19.2198 20.171C18.8879 19.4157 18.7068 18.6235 18.2944 17.8867C18.0531 17.4629 18.0832 16.9195 17.6407 16.4866C16.9366 15.7957 15.8001 15.3444 15.7196 14.2759C15.6593 13.3825 14.2914 13.0693 14.6535 12.0284C14.6937 11.9179 14.3819 11.5679 14.2411 11.5679C12.6922 11.6047 12.0082 10.4533 11.1936 9.61513C10.6303 9.03484 9.82567 9.41249 9.33283 8.91509C8.93051 8.50981 8.48796 7.82819 8.06553 7.82819C7.30113 7.81898 6.74794 7.46896 6.13441 7.26632C4.17311 6.60312 2.20178 6.18862 0.0896103 6.34521C0.0896103 6.34521 -0.292614 -0.259109 0.522079 0.00801086C1.08532 0.19223 1.61839 0.385662 2.16152 0.302761C3.59981 0.0816956 4.83693 1.04886 6.26516 0.800159C6.43615 0.772526 6.74794 0.919903 6.83846 1.06728C7.40171 2.02523 8.90034 1.47256 9.45353 2.48578C9.72509 2.98318 10.2783 3.19503 10.9522 3.12135C11.616 3.04766 12.4206 2.9187 12.139 3.98718C13.4264 3.82138 13.9293 4.81618 14.6736 5.44253C15.3777 6.03204 16.0918 6.63075 16.6852 7.31237C17.1277 7.81898 17.6809 7.72687 18.224 7.81898C18.1536 8.39006 18.2844 8.85062 18.8074 9.24669C19.3606 9.6704 19.9138 10.1217 20.3161 10.6468C20.6882 11.135 20.7888 11.7982 21.161 12.2863C21.8851 13.2074 21.9455 14.5338 23.2128 15.1233C23.253 15.7773 23.4341 16.3116 23.9872 16.8182C24.3393 17.1406 24.3292 17.7669 24.5807 18.2183C25.0735 19.1025 24.993 19.8486 24.1381 20.4934C23.6754 20.8434 23.7056 21.2118 24.1482 21.5527C24.631 21.9211 24.9629 22.3264 24.8522 22.9435C24.8522 22.9712 25.1741 23.0817 25.3551 23.0817C25.4356 23.0817 25.5764 22.9527 25.5965 22.8606C25.9284 21.5711 26.7129 20.4013 26.8538 19.0657C27.0046 17.7024 27.6584 16.3945 27.3667 14.9852C27.9501 14.2483 27.8797 13.4009 27.8797 12.5535C27.8897 11.0428 28.3926 10.7112 30.022 11.135C30.2232 11.1902 30.3841 11.3929 30.5852 11.4666C30.9071 11.5863 30.9976 11.4573 30.9172 11.1257C30.7462 10.4533 31.2491 10.527 31.7117 10.5454C32.1241 10.5547 32.1644 10.527 32.4057 11.2086L32.4115 11.2251C32.4874 11.4429 32.5146 11.5209 32.8483 11.485C33.5423 11.4205 33.8843 11.6968 33.9748 12.3784C34.1659 13.7877 33.2003 15.0589 33.4518 16.4589C32.6572 17.2879 33.0394 18.2919 32.9388 19.2223L32.9378 19.232C32.9077 19.526 32.8661 19.9331 32.6572 20.0789C32.0034 20.5394 31.8324 21.1013 31.933 21.7921C32.1141 22.9896 31.5408 24.0396 31.239 25.145C30.9675 26.1398 30.3539 27.024 30.374 28.1201C30.3841 28.811 30.0421 29.511 29.7907 30.1834C29.5392 30.8558 28.8553 31.5374 28.9559 32.1361C29.1872 33.5639 28.3725 34.3652 27.3969 35.1942C26.6124 35.8666 25.858 36.5667 25.1137 37.2851C24.6008 37.7733 23.6754 37.2851 23.253 37.9852C23.2228 38.0312 22.7702 37.9667 22.6395 37.8378C22.1265 37.368 21.6538 37.0364 20.8291 37.0088C20.3764 36.9904 20.0244 36.2351 19.5215 35.9772Z"
                                                fill="#A765DA"/>
                                        </svg>
                                    </div>
                                    <button className={`${styles.toolBtn} ${styles.primaryCta}`}
                                            onClick={handleStartVoting}>
                                        {t("startVoting")}
                                    </button>
                                </div>
                            )}
                            <div className={styles.iterationHeader}>
                                <div className={styles.iterationTitle}>
                                    <svg width="16" height="12" viewBox="0 0 16 12" fill="none"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M5 1.5H14.75M5 6H14.75M5 10.5H14.75M1.25 1.5H1.2575M1.25 6H1.2575M1.25 10.5H1.2575"
                                            stroke="#484848" strokeWidth="1.5" strokeLinecap="round"
                                            strokeLinejoin="round"/>
                                    </svg>
                                    {t("iteration", {index: iterationDoc.index + 1})}
                                </div>

                                <div className={styles.controls}>
                                    <button onClick={handlePrev} disabled={!canNavigate || iterationDoc.index === 0}>
                                        <svg width="6" height="12" viewBox="0 0 6 12" fill="none"
                                             xmlns="http://www.w3.org/2000/svg">
                                            <path d="M5.25 1.5L0.75 6L5.25 10.5" stroke="#A765DA" stroke-width="1.5"
                                                  stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg>
                                        {t("previousIterationButton")}
                                    </button>
                                    <button onClick={handleNext}
                                            disabled={!canNavigate || iterationDoc.index >= totalIterations - 1}>
                                        {t("nextIterationButton")}
                                        <svg width="6" height="12" viewBox="0 0 6 12" fill="none"
                                             xmlns="http://www.w3.org/2000/svg">
                                            <path d="M0.75 10.5L5.25 6L0.75 1.5" stroke="#A765DA" stroke-width="1.5"
                                                  stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg>
                                    </button>
                                    <button onClick={handleReveal}
                                            disabled={!isFacilitator || iterationDoc.status !== "voting"}>
                                        {t("revealResults")}
                                    </button>
                                    <button onClick={handleFinish} disabled={!isFacilitator}>
                                        {t("finishVoting")}
                                    </button>
                                </div>
                            </div>
                            <div className={styles.taskGrid}>
                                {tasks.map((task) => {
                                    const revealed = iterationDoc.status !== "voting";
                                    const votes = iterationDoc.estimatesByTask?.[task.id];
                                    const myVote = votes?.[userId] ?? null;

                                    if (!revealed) {
                                        return (
                                            <div
                                                key={task.id}
                                                className={styles.taskCard}
                                                onMouseEnter={() => setHoveredTaskId(task.id)}
                                                onMouseLeave={() => setHoveredTaskId((prev) => (prev === task.id ? null : prev))}
                                            >
                                                <div className={styles.taskContentWrp}>
                                                    <div className={styles.taskIndex}>{task.iterationIndex + 1}</div>
                                                    <div className={styles.taskText}>
                                                        <div className={styles.taskTitle}>{task.title}</div>
                                                        <div className={styles.taskDesc}>{task.description}</div>
                                                    </div>
                                                </div>
                                                {myVote != null &&
                                                    <div className={styles.myVotePill}>
                                                        <svg width="12" height="8" viewBox="0 0 12 8" fill="none"
                                                             xmlns="http://www.w3.org/2000/svg">
                                                            <path
                                                                d="M11.3546 0.146894C11.4012 0.193339 11.4381 0.248515 11.4633 0.30926C11.4886 0.370005 11.5015 0.435127 11.5015 0.500894C11.5015 0.566661 11.4886 0.631782 11.4633 0.692528C11.4381 0.753273 11.4012 0.808448 11.3546 0.854894L4.35463 7.85489C4.30819 7.90146 4.25301 7.9384 4.19227 7.96361C4.13152 7.98881 4.0664 8.00179 4.00063 8.00179C3.93486 8.00179 3.86974 7.98881 3.809 7.96361C3.74825 7.9384 3.69308 7.90146 3.64663 7.85489L0.146631 4.35489C0.0527448 4.26101 0 4.13367 0 4.00089C0 3.86812 0.0527448 3.74078 0.146631 3.64689C0.240518 3.55301 0.367856 3.50026 0.500632 3.50026C0.633407 3.50026 0.760745 3.55301 0.854632 3.64689L4.00063 6.79389L10.6466 0.146894C10.6931 0.100331 10.7483 0.0633878 10.809 0.0381813C10.8697 0.0129749 10.9349 0 11.0006 0C11.0664 0 11.1315 0.0129749 11.1923 0.0381813C11.253 0.0633878 11.3082 0.100331 11.3546 0.146894Z"
                                                                fill="#484848"/>
                                                        </svg>
                                                        Ваша оценка: <b>{String(myVote)}</b>
                                                    </div>}

                                                {(hoveredTaskId === task.id) && setupStatus === "inProgress"
                                                    && myVote == null && !isFacilitator && (
                                                        <div className={styles.hoverPanel}>
                                                            <EstimatePanel
                                                                estimationType={estimationType}
                                                                customValues={customValues}
                                                                isRevealed={false}
                                                                selectedVote={myVote ?? null}
                                                                onVote={(v) => handleVote(task.id, v)}
                                                                classes={{
                                                                    grid: styles.estimateGrid,
                                                                    button: styles.estimateBtn,
                                                                    buttonSelected: styles.estimateBtnSelected,
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                            </div>
                                        );
                                    }

                                    // revealed
                                    return (
                                        <ResultCard
                                            key={task.id}
                                            task={task}
                                            stats={votes ? VoteStats.calcStatsFromVotes(votes, getScaleValues(estimationType, customValues)) : {}}
                                            hovered={hoveredResultTaskId === task.id}
                                            onHover={(id) => setHoveredResultTaskId(id)}
                                            myVote={myVote}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                        <ParticipantsList
                            participants={participants}
                            currentUserId={userId}
                            votedMap={votedMap}
                        />
                    </>
                )}

                {setupStatus === "finished" && (
                    <div className={styles.resultsGrid}>
                        {[...Array(totalIterations)].map((_, ix) => (
                            <div key={ix} className={styles.iterationBlock}>
                                <div className={styles.iterationHeader}>
                                    <div className={styles.iterationTitle}>
                                        <svg width="16" height="12" viewBox="0 0 16 12" fill="none"
                                             xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M5 1.5H14.75M5 6H14.75M5 10.5H14.75M1.25 1.5H1.2575M1.25 6H1.2575M1.25 10.5H1.2575"
                                                stroke="#484848" strokeWidth="1.5" strokeLinecap="round"
                                                strokeLinejoin="round"/>
                                        </svg>
                                        {t("iteration", {index: ix + 1})}
                                    </div>
                                    <button
                                        className={styles.revoteButton}
                                        onClick={() => handleRevoteIteration(ix)}
                                        disabled={!isFacilitator}
                                    >
                                        <svg width="14" height="12" viewBox="0 0 14 12" fill="none"
                                             xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M0.583008 1.33391V4.83391M0.583008 4.83391H4.08301M0.583008 4.83391L3.28967 2.29057C3.91661 1.66332 4.69223 1.20511 5.54415 0.958691C6.39607 0.712272 7.29653 0.685678 8.16151 0.88139C9.02649 1.0771 9.82779 1.48874 10.4907 2.0779C11.1535 2.66706 11.6563 3.41453 11.9522 4.25057M13.4163 10.6672V7.16724M13.4163 7.16724H9.91634M13.4163 7.16724L10.7097 9.71057C10.0827 10.3378 9.30712 10.796 8.4552 11.0425C7.60328 11.2889 6.70282 11.3155 5.83784 11.1198C4.97286 10.924 4.17156 10.5124 3.5087 9.92325C2.84584 9.33409 2.34302 8.58662 2.04717 7.75057"
                                                stroke="#A765DA" stroke-width="1.16667" stroke-linecap="round"
                                                stroke-linejoin="round"/>
                                        </svg>
                                        {t("revote")}
                                    </button>
                                </div>
                                <div className={styles.iterationGrid}>
                                    {allTasks
                                        .filter((t) => t.iterationId === String(ix))
                                        .sort((a, b) => (a.iterationIndex ?? 0) - (b.iterationIndex ?? 0))
                                        .map((task) => {
                                            const agg = aggregatesByTask[task.id];
                                            const myVote = agg?.votes?.[userId] ?? null;
                                            return (
                                                <ResultCard
                                                    key={task.id}
                                                    task={task}
                                                    stats={agg?.stats ?? {}}
                                                    hovered={hoveredResultTaskId === task.id}
                                                    onHover={(id) => setHoveredResultTaskId(id)}
                                                    myVote={myVote}
                                                />
                                            );
                                        })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {showTaskManager && (
                    <TaskManagerModal roomId={roomId} onClose={() => setShowTaskManager(false)}/>
                )}

                <RoomSettingsModal
                    open={settingsOpen}
                    onClose={() => setSettingsOpen(false)}
                    roomName={roomName}
                    onRoomNameChange={setRoomName}
                    estimationType={estimationType}
                    onEstimationTypeChange={setEstimationType}
                    showApplyButton={true}
                    roomId={roomId!}
                    mode={"withFacilitator"} // Bucket work only WITH facilitator
                    participants={participants}
                    userId={userId}
                    ownerId={ownerId}
                    roomType="bucket"
                />
            </div>
            <div className={styles.footer}>
                <img src={footerBg}/>
                <div className={styles.footerContent}>
                    <svg width="20" height="15" viewBox="0 0 20 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M18.6894 3.63159C18.3967 2.94165 17.9576 2.32698 17.3722 1.78757C16.7995 1.23562 16.0677 0.802838 15.1768 0.48923C14.2986 0.163077 13.2678 0 12.0842 0H0V14.1124H7.94156V11.7227H7.96065L7.97974 11.7039H12.0842C13.2423 11.7039 14.2604 11.5471 15.1386 11.2335C16.0168 10.9073 16.7486 10.4683 17.334 9.91631C17.9321 9.36435 18.3776 8.73714 18.6703 8.03465C18.9758 7.31963 19.1285 6.56697 19.1285 5.77667C19.1285 5.03655 18.9821 4.32153 18.6894 3.63159ZM7.95174 9.47349L7.94156 9.49482L7.93011 9.47349C7.22504 7.99075 5.13784 7.42751 5.13784 5.83438C5.13784 4.4131 7.14995 3.85739 7.94156 5.28995C8.7319 3.85739 10.744 4.4131 10.744 5.83438C10.744 7.42751 8.65681 7.99075 7.95174 9.47349Z"
                            fill="#9B9B9B"/>
                    </svg>
                    <p>
                        {t("footerDescription")}
                    </p>
                </div>
                <p className={styles.copyright}>
                    2026 © Made with ❤️ from us. All rights reserved.
                </p>
            </div>
        </div>
    );
};

export default BucketRoomPage;

